import SelectLookUpControl from '../Components/SelectLookUpControl'
import { Box } from '@mui/material'

import { useEffect, useState } from 'react'
import FieldTypePagination from '../Components/SelectLookUpControl/FieldTypePagination'
import {
    DEFAULT_LOOKUP_ATTRIBUTES,
    DEFAULT_PAGE_SIZE_DETAILS,
    useDebounce,
} from 'packages/core'
import { getFieldTypeRequiredErrorMessage } from 'packages/core'
import { paginate, sortArrayObjects } from 'utils'

const selectionIdentifier = 'id'
const SingleSelectLookupControl = ({
    fieldType,
    isFormValidated,
    valueType,
    isApiSupportedSource,
    handleUpdate,
    useDataHook,
}) => {
    const [attributes, setAttribues] = useState(DEFAULT_LOOKUP_ATTRIBUTES)
    const [searchKey, setSearchKey] = useState('')
    const debouncedValue = useDebounce(searchKey)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_DETAILS)
    const [page, setPage] = useState(1)
    const [sourceValues, setSourceValues] = useState([])
    const [showFieldValues, setShowFieldValues] = useState(true)
    const [errorMessage, setErrorMessage] = useState('')
    const [sourceUpdated, setSourceUpdated] = useState(false)
    const [sorting, setSorting] = useState({ sortBy: '', sortOrder: '' })

    useEffect(() => {
        if (fieldType?.FieldParameters) {
            const fieldParams = JSON.parse(fieldType?.FieldParameters)
            const columns = fieldParams?.ColumnsMapping
            if (columns?.length) {
                const fieldAttribute = columns.map((item) => {
                    return {
                        name: item?.Key,
                        label: item?.DataFieldName,
                    }
                })
                setAttribues(fieldAttribute)
            }
        }
    }, [fieldType?.FieldParameters])

    useEffect(() => {
        if (fieldType?.SourceValues && sourceUpdated) {
            setShowFieldValues(
                fieldType?.SourceValues?.length || searchKey ? true : false,
            )
            setSourceValues(setRefinedList(fieldType?.SourceValues || []))
        }
    }, [fieldType?.SourceValues, searchKey, sourceUpdated])

    useEffect(() => {
        if (isFormValidated && !fieldType?.[valueType]) {
            setErrorMessage(getFieldTypeRequiredErrorMessage(fieldType))
        } else {
            setErrorMessage('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFormValidated, fieldType?.[valueType]])

    const { data: sourceData, isFetching } = useDataHook(
        fieldType,
        (page - 1) * pageSize,
        pageSize,
        debouncedValue,
        sorting,
    )

    useEffect(() => {
        if (isApiSupportedSource) {
            if (!isFetching) {
                updateSourceValues()
                setSourceUpdated(true)
            }
        } else {
            setShowFieldValues(fieldType?.SourceValues?.length ? true : false)
            setSourceValues(setRefinedList(fieldType?.SourceValues || []))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isApiSupportedSource, isFetching])

    const updateSourceValues = () => {
        const valueToUpdate = {
            ...fieldType,
            SourceValues: sourceData?.data || [],
        }
        handleUpdate(valueToUpdate)
    }

    const handleSort = (sortByName, sortOrder) => {
        if (!isApiSupportedSource) {
            const sortedValues = sortArrayObjects(
                sourceValues,
                sortByName,
                sortOrder,
            )
            setSourceValues(sortedValues)
        } else {
            setSorting({
                sortBy: sortByName,
                sortOrder: sortOrder,
            })
        }
    }

    const setRefinedList = (listData) => {
        try {
            if (!listData?.length) {
                return []
            }

            const isIdExist = Boolean(listData[0]?.id) || false
            if (isIdExist) {
                return listData
            }
            const updatedList = listData.map((item, index) => {
                return {
                    ...item,
                    id: index?.toString(),
                }
            })
            return updatedList
        } catch (err) {}
    }

    const handleSelection = (item) => {
        const selected = [item.id]
        const selectedValue = selected.map((item) => {
            return sourceValues.find((x) => x[selectionIdentifier] === item)
        })

        const valueToUpdate = {
            ...fieldType,
            [valueType]: selectedValue ? selectedValue[0] : null,
        }
        handleUpdate(valueToUpdate)
    }

    const handleSearch = (val) => {
        setSearchKey(val)
        setPage(1)
    }

    const handlePageChange = (_, value) => {
        setPage(value)
    }

    const bindSelectedValue = () => {
        try {
            const colKeyName = attributes[0]?.name
            if (!fieldType?.[valueType]) {
                return []
            }
            return (
                [fieldType?.[valueType]]?.map((item) => {
                    const selectVal = getPaginatedItems()?.items?.find(
                        (x) => x[colKeyName] === item[colKeyName],
                    )
                    return selectVal?.id
                }) || []
            )
        } catch (err) {
            return []
        }
    }

    const getPaginatedItems = () => {
        if (isApiSupportedSource) {
            return { items: sourceValues, totalCount: sourceData.totalCount }
        }
        const attributeName = attributes[0].name
        const filtered = sourceValues.filter((item) =>
            item?.[attributeName]
                ?.toLowerCase()
                .includes(searchKey.toLowerCase()),
        )
        return {
            items: paginate(filtered, page, pageSize),
            totalCount: filtered.length,
        }
    }
    return (
        <Box
            sx={{
                background: '#fbfbfd',
                margin: '0',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            <SelectLookUpControl
                attributes={attributes}
                showFieldValues={showFieldValues || attributes?.length}
                selectedItems={bindSelectedValue()}
                items={getPaginatedItems()?.items || []}
                title={fieldType?.Name || ''}
                fieldTypeScope={fieldType?.FieldTypeScope}
                multiSelect={false}
                handleRowSelection={handleSelection}
                selectionIdentifier={selectionIdentifier}
                isInitialLoading={isFetching}
                searchKey={searchKey}
                handleSearch={handleSearch}
                rowSelection={true}
                handleSort={handleSort}
                pagination={
                    <FieldTypePagination
                        pageSize={pageSize}
                        page={page}
                        setPageSize={(pageSize) => {
                            setPage(1)
                            setPageSize(pageSize)
                        }}
                        handlePageChange={handlePageChange}
                        numberOfPages={
                            Math.ceil(
                                getPaginatedItems()?.totalCount
                                    ? getPaginatedItems()?.totalCount / pageSize
                                    : 0,
                            ) || 0
                        }
                        totalCount={getPaginatedItems()?.totalCount || 0}
                    />
                }
                isRequired={fieldType?.IsRequired}
                errorMessage={errorMessage}
                isDisabled={fieldType?.IsReadOnly}
            />
        </Box>
    )
}

export default SingleSelectLookupControl
